import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { AssetIconComponent } from '@stillmandigital/assets-icons';
import { SharedStore } from '@shared/store/shared.signal-store';
import { matchText } from '@shared/utils/strings';
import { getBaseAssetCode, getQuoteAssetCode } from '@shared/utils/assets';
import {
  LocalSettingsService,
  RecentSearchEntry,
  RecentSearchType,
  SearchResultIcon,
} from '@shared/services/local-settings.service';
import { DEFAULT_VISIBLE_ACCOUNT_TYPES } from 'app/accounts/components/accounts/accounts.utils';
import { getAllMenuItems } from '../sidebar/sidebar.utils';
import { MarketMakingPlatformsStore } from 'app/market-making/shared/market-making-platforms.store';

interface SearchResult {
  label: string;
  groupPrefix?: string;
  sublabel?: string;
  icon?: SearchResultIcon;
  navigate: () => void;
  recent: RecentSearchEntry;
}

interface SearchResultGroup {
  label: string;
  results: SearchResult[];
}

const ACCOUNT_ICON: SearchResultIcon = { kind: 'material', ligature: 'account_balance' };
const ADMIN_ICON: SearchResultIcon = { kind: 'material', ligature: 'person' };
const LIQUIDITY_PROVIDER_ICON: SearchResultIcon = { kind: 'material', ligature: 'waves' };

@Component({
  selector: 'app-global-search-palette',
  templateUrl: './global-search-palette.component.html',
  styleUrl: './global-search-palette.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, AssetIconComponent],
})
export class GlobalSearchPaletteComponent {
  private readonly _router = inject(Router);
  private readonly _dialogRef = inject(MatDialogRef<GlobalSearchPaletteComponent>);
  private readonly _store = inject(SharedStore);
  private readonly _localSettings = inject(LocalSettingsService);

  private readonly _menuItems = getAllMenuItems(inject(MarketMakingPlatformsStore).platforms());

  public readonly query = signal('');

  public readonly selectedIndex = signal(0);

  public readonly resultGroups = computed<SearchResultGroup[]>(() => {
    const term = this.query().trim();

    if (!term) {
      const recentResults = this._localSettings.recentSearches().map(entry => this._resultFromRecent(entry));
      return recentResults.length ? [{ label: 'Recent', results: recentResults }] : [];
    }

    const groups: SearchResultGroup[] = [
      {
        label: 'Pages',
        results: this._menuItems
          .filter(item => matchText(item.name, term))
          .flatMap(item => [item, ...(item.children ?? [])])
          .filter((item, index, items) => items.findIndex(other => other.fullPath === item.fullPath) === index)
          .map(item => {
            const sublabel = item.children?.length ? 'Section' : undefined;
            const groupPrefix = item.groupPath?.length ? item.groupPath.join(' › ') : undefined;
            const icon: SearchResultIcon = {
              kind: 'material',
              ligature: item.children?.length ? 'folder' : 'description',
            };
            return {
              label: item.name,
              groupPrefix,
              sublabel,
              icon,
              navigate: () => this._navigateToPage(item.fullPath!),
              recent: {
                type: 'page' as const,
                id: item.fullPath!,
                label: item.name,
                groupPrefix,
                sublabel,
                icon,
              },
            };
          }),
      },
      {
        label: 'Accounts',
        results: this._store
          .accounts()
          .filter(account => matchText(account.name, term))
          .filter(account => account.accountType && DEFAULT_VISIBLE_ACCOUNT_TYPES.includes(account.accountType))
          .map(account => ({
            label: account.name!,
            sublabel: 'Accounts',
            icon: ACCOUNT_ICON,
            navigate: () => this._navigateToAccount(account.id!),
            recent: {
              type: 'account' as const,
              id: `${account.id}`,
              label: account.name!,
              sublabel: 'Accounts',
              icon: ACCOUNT_ICON,
            },
          })),
      },
      {
        label: 'Admins',
        results: this._store
          .admins()
          .filter(
            user => matchText(user.firstName, term) || matchText(user.lastName, term) || matchText(user.login, term),
          )
          .map(user => {
            const label =
              user.firstName || user.lastName ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : user.login!;
            const sublabel = `${user.login} — Admins`;
            return {
              label,
              sublabel,
              icon: ADMIN_ICON,
              navigate: () => this._navigateToList('/users', user.id!),
              recent: { type: 'user' as const, id: `${user.id}`, label, sublabel, icon: ADMIN_ICON },
            };
          }),
      },
      {
        label: 'Pairs',
        results: this._store
          .basePairs()
          .filter(pair => matchText(pair.name, term))
          .map(pair => {
            const icon: SearchResultIcon = {
              kind: 'pair',
              baseCode: getBaseAssetCode(pair.name),
              quoteCode: getQuoteAssetCode(pair.name),
            };
            return {
              label: pair.name!,
              icon,
              navigate: () => this._navigateToPair(pair.id!),
              recent: { type: 'pair' as const, id: `${pair.id}`, label: pair.name!, icon },
            };
          }),
      },
      {
        label: 'Assets',
        results: this._store
          .assets()
          .filter(asset => matchText(asset.code, term) || matchText(asset.name, term))
          .map(asset => {
            const sublabel = asset.name ? `${asset.name} — Settings › Assets` : 'Settings › Assets';
            const icon: SearchResultIcon = { kind: 'asset', code: asset.code! };
            return {
              label: asset.code!,
              sublabel,
              icon,
              navigate: () => this._navigateToList('/settings/assets', asset.id!),
              recent: { type: 'asset' as const, id: `${asset.id}`, label: asset.code!, sublabel, icon },
            };
          }),
      },
      {
        label: 'Liquidity Providers',
        results: this._store
          .liquidityProviders()
          .filter(lp => matchText(lp.name, term) || matchText(lp.shortName, term))
          .map(lp => ({
            label: lp.name!,
            sublabel: 'Settings › Liquidity providers',
            icon: LIQUIDITY_PROVIDER_ICON,
            navigate: () => this._navigateToList('/settings/liquidity-providers', lp.id!),
            recent: {
              type: 'liquidityProvider' as const,
              id: `${lp.id}`,
              label: lp.name!,
              sublabel: 'Settings › Liquidity providers',
              icon: LIQUIDITY_PROVIDER_ICON,
            },
          })),
      },
    ];

    return groups.filter(group => group.results.length);
  });

  public readonly flatResults = computed(() => this.resultGroups().flatMap(group => group.results));

  constructor() {
    if (!this._store.accountsLoaded()) {
      this._store.loadAccounts();
    }
    if (!this._store.usersLoaded()) {
      this._store.loadUsers();
    }
    if (!this._store.pairsLoaded()) {
      this._store.loadPairs();
    }
    if (!this._store.assetsLoaded()) {
      this._store.loadAssets();
    }
    if (!this._store.liquidityProvidersLoaded()) {
      this._store.loadLiquidityProviders();
    }
  }

  public onQueryChange(value: string) {
    this.query.set(value);
    this.selectedIndex.set(0);
  }

  public isSelected(result: SearchResult): boolean {
    return this.flatResults()[this.selectedIndex()] === result;
  }

  public hover(result: SearchResult) {
    const index = this.flatResults().indexOf(result);
    if (index !== -1) {
      this.selectedIndex.set(index);
    }
  }

  public select(result: SearchResult) {
    result.navigate();
    this._localSettings.addRecentSearch(result.recent);
    this._dialogRef.close();
  }

  public onKeydown(event: KeyboardEvent) {
    const results = this.flatResults();

    if (!results.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.selectedIndex.update(i => (i + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.selectedIndex.update(i => (i - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.select(results[this.selectedIndex()]);
    }
  }

  private _resultFromRecent(entry: RecentSearchEntry): SearchResult {
    return {
      label: entry.label,
      groupPrefix: entry.groupPrefix,
      sublabel: entry.sublabel,
      icon: entry.icon,
      navigate: () => this._navigateForRecentType(entry.type, entry.id),
      recent: entry,
    };
  }

  private _navigateForRecentType(type: RecentSearchType, id: string) {
    switch (type) {
      case 'page':
        return this._navigateToPage(id);
      case 'account':
        return this._navigateToAccount(id);
      case 'user':
        return this._navigateToList('/users', id);
      case 'pair':
        return this._navigateToPair(id);
      case 'asset':
        return this._navigateToList('/settings/assets', id);
      case 'liquidityProvider':
        return this._navigateToList('/settings/liquidity-providers', id);
    }
  }

  private _navigateToPage(fullPath: string) {
    void this._router.navigateByUrl(fullPath);
  }

  private _navigateToList(url: string, highlightId: string | number) {
    void this._router.navigate([url], { queryParams: { highlightId } });
  }

  private _navigateToAccount(accountId: string | number) {
    void this._router.navigate(['/accounts', 'details', accountId, 'balances'], {
      queryParams: { highlightId: accountId },
      queryParamsHandling: 'merge',
    });
  }

  private _navigateToPair(pairId: string | number) {
    void this._router.navigate(['/settings', 'pairs', pairId]);
  }
}
